import type { PaginationInstance } from '@tanstack/react-table';
import type { CoreInstance } from '@tanstack/table-core';
import type { ReactElement } from 'react';
import { pageOptions } from './UseTablePaginator.tsx';
import { Select } from '../inputs/index.ts';
import Pagination from '@mui/material/Pagination';
import type { SelectProps } from '../inputs/Select/Select.props.ts';
import { Box } from '@mui/joy';
import { PaginationItem } from '@mui/material';
import { ICursorType } from 'generated/graphql.tsx';

const calculateMaxPageNumber = (totalResults: number | undefined, pageSize: number): number => {
  return Math.ceil((totalResults ?? 0) / pageSize);
};

const pageSelectProps = {
  width: 'normal',
  options: pageOptions,
} satisfies Partial<SelectProps<string>>;

export const GTablePagePagination = <ROW,>(props: {
  table: CoreInstance<ROW> & PaginationInstance<ROW>;
  totalResults: number | undefined;
  hideRowsPerPage?: boolean;
}): ReactElement => {
  const maxPageNumber = calculateMaxPageNumber(props.totalResults, props.table.getState().pagination.pageSize);

  return (
    <>
      {!props.hideRowsPerPage && (
        <Select
          {...pageSelectProps}
          value={props.table.getState().pagination.pageSize.toString()}
          onChange={(value): void => {
            props.table.setPagination({
              pageIndex: Math.floor(
                (props.table.getState().pagination.pageIndex * props.table.getState().pagination.pageSize) /
                  Number.parseInt(value)
              ),
              pageSize: Number.parseInt(value),
            });
          }}
        />
      )}
      <Pagination
        sx={{
          marginLeft: 'auto !important',
        }}
        count={maxPageNumber}
        page={props.table.getState().pagination.pageIndex + 1}
        onChange={(_e, page) => {
          props.table.setPagination({
            pageIndex: page - 1,
            pageSize: props.table.getState().pagination.pageSize,
          });
        }}
      />
    </>
  );
};

export const GTableCursorPagination = <ROW,>(props: {
  table: CoreInstance<ROW> & PaginationInstance<ROW>;
  hideRowsPerPage?: boolean;
}): ReactElement => {
  return (
    <>
      {!props.hideRowsPerPage && (
        <Select
          value={props.table.getState().pagination.pageSize.toString()}
          {...pageSelectProps}
          onChange={(value): void => {
            props.table.setPagination({
              ...props.table.getState().pagination,
              pageSize: Number.parseInt(value),
            });
          }}
        />
      )}
      <Box
        component="nav"
        sx={{
          marginLeft: 'auto !important',
        }}
      >
        <PaginationItem
          type="first"
          disabled={!props.table.getState().pagination.hasPreviousPage}
          onClick={() => {
            props.table.setPagination({
              ...props.table.getState().pagination,
              cursorType: ICursorType.First,
              cursorValue: null,
              first: null,
              last: null,
              hasPreviousPage: false,
              hasNextPage: false,
            });
          }}
        />
        <PaginationItem
          type="previous"
          disabled={!props.table.getState().pagination.hasPreviousPage}
          onClick={() => {
            props.table.setPagination({
              ...props.table.getState().pagination,
              cursorType: ICursorType.Before,
              cursorValue: props.table.getState().pagination.first,
              first: null,
              last: null,
              hasPreviousPage: false,
              hasNextPage: false,
            });
          }}
        />
        <PaginationItem
          type="next"
          disabled={!props.table.getState().pagination.hasNextPage}
          onClick={() => {
            props.table.setPagination({
              ...props.table.getState().pagination,
              cursorType: ICursorType.After,
              cursorValue: props.table.getState().pagination.last,
              first: null,
              last: null,
              hasPreviousPage: false,
              hasNextPage: false,
            });
          }}
        />
        <PaginationItem
          type="last"
          disabled={!props.table.getState().pagination.hasNextPage}
          onClick={() => {
            props.table.setPagination({
              ...props.table.getState().pagination,
              cursorType: ICursorType.Last,
              cursorValue: null,
              first: null,
              last: null,
              hasPreviousPage: false,
              hasNextPage: false,
            });
          }}
        />
      </Box>
    </>
  );
};
